import { createContext, useContext, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { CanvasCamera } from "@/lib/canvas-camera";
import type { CanvasView as View } from "@/lib/viewport";

const CameraContext = createContext<CanvasCamera | null>(null);
const noSubscription = () => () => {};

export function useCanvasZoom(fallback = 1) {
  const camera = useContext(CameraContext);
  const read = () => camera?.getSnapshot().view.zoom ?? fallback;
  return useSyncExternalStore(camera?.subscribe || noSubscription, read, read);
}

export function useCanvasCamera(view: View, onCommit?: (view: View) => void, scope?: string | null) {
  const camera = useMemo(() => new CanvasCamera(view, onCommit), [scope]);
  useLayoutEffect(() => { camera.onCommit = onCommit; });
  // An unrelated parent render must never replace a gesture with the last saved view.
  useLayoutEffect(() => { camera.restore(view); }, [camera, view.zoom, view.pan.x, view.pan.y]);
  useLayoutEffect(() => {
    const hide = () => { if (document.visibilityState === "hidden") camera.flush(); };
    window.addEventListener("pagehide", camera.flush);
    document.addEventListener("visibilitychange", hide);
    return () => {
      window.removeEventListener("pagehide", camera.flush);
      document.removeEventListener("visibilitychange", hide);
      camera.flush();
    };
  }, [camera]);
  return camera;
}

/** Children retain their identity while only their containing transform changes. */
export function CanvasStage({ camera, className = "pf-artwork", children }: {
  camera: CanvasCamera;
  className?: string;
  children: ReactNode;
}) {
  const { view, moving } = useSyncExternalStore(camera.subscribe, camera.getSnapshot, camera.getSnapshot);
  return <CameraContext.Provider value={camera}><div className={className} style={{
    transform: `translate(${view.pan.x}px,${view.pan.y}px) scale(${view.zoom})`,
    // Cache only while moving; release the layer to repaint crisp text at rest.
    willChange: moving ? "transform" : undefined,
  }}>{children}</div></CameraContext.Provider>;
}

/** Screen-sized pins, handles and controls subscribe without rendering the artwork. */
export function CanvasView({ camera, children }: {
  camera: CanvasCamera;
  children: (view: View) => ReactNode;
}) {
  const { view } = useSyncExternalStore(camera.subscribe, camera.getSnapshot, camera.getSnapshot);
  return children(view);
}
